import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai/index';
import { use } from 'react';

import { TradingLeaderboardInterval } from '@flipster/apis/v2/trading-leaderboards/types';
import { FormattedMessage } from '@flipster/i18n';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { Spinner } from '@flipster/react/design-system/spinner';
import { Datetime } from '@flipster/react/design-utils/datetime';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, square, stack, vstack } from '@flipster/styled-system/patterns';
import { DateFormatter } from '@flipster/utils/datetime';

import { LeaderboardContext, intervalLeaderboardBunja, intervalLeaderboardMyRankingBunja } from '../bunja';
import { RankItem } from './item';

const intervalAlertMap: Record<TradingLeaderboardInterval, { title: string; desc: string }> = {
  WEEKLY: {
    title: 'Weekly P&L leaderboard',
    desc: 'The leaderboard resets every monday at midnight UTC.\nRanking updates every 10 minutes.',
  },
  MONTHLY: {
    title: 'Monthly P&L leaderboard',
    desc: 'The leaderboard resets at midnight UTC on the first day of each month.\nRanking updates every 10 minutes.',
  },
};

interface RankListProps {
  css: Styles;
}
export const RankList = ({ css: cssProps }: RankListProps) => {
  const { interval } = use(LeaderboardContext);
  const { rankingsAtom, lastUpdatedAtAtom } = useBunja(intervalLeaderboardBunja);
  const { currentCursorRankingAtom } = useBunja(intervalLeaderboardMyRankingBunja);
  const rankings = useAtomValue(rankingsAtom);
  const myRanking = useAtomValue(currentCursorRankingAtom);
  const lastUpdatedAt = useAtomValue(lastUpdatedAtAtom);

  const handleInfoClick = () => {
    const { title, desc } = intervalAlertMap[interval];
    dialog.open({
      title: {
        label: title ?? 'Notice',
      },
      content: (
        <div className={stack({ gap: 16 })}>
          <p>{desc}</p>
          <p>
            <span>
              <FormattedMessage id='Autogen_common.r251126.s_Latest_update' />
            </span>
            <Datetime
              value={lastUpdatedAt}
              format={DateFormatter.ShortYearDateTime}
              isUTC
              withUTCText
              css={{
                textStyle: 'Legacy/Number2',
              }}
            />
          </p>
        </div>
      ),
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label='OK' />,
      },
    });
  };

  return (
    <div
      className={css(
        stack.raw({
          tabletDown: {
            mx: 12,
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderColor: 'border.default.level-1',
          },
          tablet: {
            p: 24,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'border.default.level-1',
            rounded: 8,
          },
        }),
        cssProps,
      )}
    >
      <div className={headerStyles}>
        <span>
          <FormattedMessage id='Autogen_common.r251126.s_Trader' />
        </span>
        <p className={hstack({ gap: 4 })}>
          <span>
            <FormattedMessage id='Autogen_common.r251126.s_PnL_USDT' />
          </span>
          <button
            type='button'
            className={square({ size: 16, color: 'content.default.level-1' })}
            onClick={handleInfoClick}
          >
            <InformationCircleIcon size={16} />
          </button>
        </p>
      </div>
      {rankings ? (
        <div className={stack()}>
          <ul className={css({ tabletDown: { pb: 180 }, tablet: { p: 0, pt: 20 } })}>
            {rankings.map((ranking) => {
              const isMe = myRanking && ranking.nickname === myRanking.nickname;
              return (
                <RankItem
                  ranking={ranking}
                  key={ranking.userProfileId}
                  me={isMe ? { delta: ranking.dailyRankChange ?? 0 } : undefined}
                />
              );
            })}
          </ul>
        </div>
      ) : (
        <Loading />
      )}
    </div>
  );
};

const headerStyles = hstack({
  h: 39,
  py: 12,
  px: { base: 12, tablet: 24 },
  justify: 'space-between',
  color: 'content.default.level-1',
  textStyle: 'Text/M/Regular',
  tablet: {
    h: 'auto',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderColor: 'border.default.level-3',
  },
});

const Loading = () => {
  return (
    <div className={vstack({ justify: 'center', gap: 16, h: 300, tablet: { h: 600 } })}>
      <Spinner size={24} />
      <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage id='Autogen_common.r251126.s_We_are_updating_the_latest_trading_rankings' />
      </p>
    </div>
  );
};
